import type { MailboxInfo } from "owa-client-types";
import type { HeadersWithoutIterator } from "owa-service/lib/RequestOptions";
import { getQueryStringParameters } from "owa-querystring";
import { getAccountFromMsalByLoginHint, getActiveAccountFromMsal, getAllAccountFromMsal, setActiveAccountRelatedProperties, } from "./utils/MsalAccounts";
import type { AccountInfo, AuthError, AuthenticationResult, IPublicClientApplication, } from "@azure/msal-browser-1p";
import { PromptValue } from "@azure/msal-browser-1p";
import { acquireAccessTokenMsal, acquireTokenSilently } from "./acquireAccessTokenMsal";
import type { AccountSourceType } from "owa-account-source-list-types";
import { createAuthError } from "@azure/msal-common";
import { handleLoginRedirectPromise, loginUserMsalInternal } from "./loginUserMsal";
import { InteractionType } from "./utils/InteractionType";
import { getMsalInstance } from "./initializeMsalLibrary";
import { getTypeHintFromAccountInfo, getTypeHintFromDomain } from "./getTypeHint";
import { isMsalEnabledForBusiness } from "./isMsalEnabledForBusiness";
import { isMonitoringProbe } from "./isMonitoringProbe";
import { logStartCoreUsage } from "owa-analytics-start";
import getModuleContextMailboxInfo from "owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo";
import isAccountSourceListStoreInitialized from "owa-account-source-list-store/lib/utils/isAccountSourceListStoreInitialized";
import { isHosted } from "owa-config";
import { msalAuthenticationResultToAuthorizationHeader } from "./utils/msalAuthenticationResultToTokenResponse";
import getClaims from "./utils/getClaims";
import { isInteractionRequiredOrNetworkError } from "./utils/isInteractionRequiredOrNetworkError";
import { isMsalEnabledForConsumerDomain } from "./isMsalEnabledForConsumerDomain";
import { getGuid } from "owa-guid";
import { setAuthTiming } from "owa-auth-timings";
import { isMsalEnabledForConsumer } from "./isMsalEnabledForConsumer";
import { setupBackgroundTokenRefresh } from "./setupBackgroundTokenRefresh";
import { buildAuthorizationHeader } from "owa-tokenprovider-utils/lib/buildAuthorizationHeader";
import { type AuthStatus, setAuthStatusInfo } from "./authStatusInfo";
import { getItem, setItem, removeItem, itemExists } from "owa-local-storage";
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from "owa-analytics-types";
import { getMsalVersion } from "./getMsalVersion";
import { toAuthError } from "./utils/toAuthError";

// Login request during boot only needs to be processed once, cache the promise to avoid duplicate handling.
let loginPromise: Promise<string | undefined> | undefined;
export function resetLoginPromise(): void {
    loginPromise = undefined;
}

let tokenPromise: Promise<string | undefined> | undefined;
export function resetTokenPromise(): void {
    tokenPromise = undefined;
    removeItem(self, "msalexp");
}

function isMailboxInfoPopulated(mailboxInfo: MailboxInfo): boolean {
    if (mailboxInfo.mailboxSmtpAddress) {
        return true;
    }
    if (mailboxInfo.userIdentity) {
        return true;
    }
    return false;
}

export async function getAuthTokenMsal(headers?: HeadersWithoutIterator, mailboxInfo?: MailboxInfo): Promise<string | undefined> {
    if (!mailboxInfo && isAccountSourceListStoreInitialized()) {
        mailboxInfo = getModuleContextMailboxInfo();
    }
    const correlationId = getGuid();
    
    const promptValue = getPromptValue();
    const loginHint = getLoginHint();
    const claims = headers && getClaims(headers);
    
    // If mailboxInfo is available, it means user has already logged in and sessions data has been fetched, then it should acquire token silently.
    // If prompt or loginhint is present in the url, then OWA should always prompt user to the login page or fetch MSAL account using loginhint.
    if (!loginHint && !promptValue && mailboxInfo && isMailboxInfoPopulated(mailboxInfo)) {
        setupBackgroundTokenRefresh(mailboxInfo);
        
        resetLoginPromise(); // Since user has already logged in, loginPromise shouldn't be needed anymore, reset the value to prevent unexpected access.
        
        // Cache the token promise to avoid duplicate handling.
        // Always get a new token if claims are present.
        if (!tokenPromise || claims) {
            tokenPromise = acquireAccessTokenMsal(mailboxInfo, "GetAuthTokenMsal", undefined /*resource*/, undefined /*scope*/, headers, correlationId).then(async (tokenResponse) => {
                resetTokenPromise();
                
                if (tokenResponse?.ExpiresIn) {
                    var tokenExpiry = tokenResponse.ExpiresIn + Date.now();
                    setItem(self, "msalexp", tokenExpiry.toString());
                }
                
                return tokenResponse
                    ? buildAuthorizationHeader(tokenResponse.AccessToken)
                    : undefined;
            });
        }
        
        return tokenPromise;
    }
    
    // Cache the login promise to avoid duplicate handling.
    // Always get a new token if claims are present.
    if (!loginPromise || claims) {
        loginPromise = getAuthTokenForStartupData(correlationId, loginHint, claims, promptValue).then(async (result) => {
            return buildAuthorizationHeader(result);
        });
    }
    
    return loginPromise;
}

async function getAuthTokenForStartupData(correlationId: string, username?: string, claims?: string, promptValue?: string): Promise<string | undefined> {
    const msalInstance = await getMsalInstance();
    const isRunningHosted = isHosted();
    const domainTypeHint = getTypeHintFromDomain();
    const startTime = self.performance.now();
    const activeAccount = getActiveAccountFromMsal(msalInstance);
    
    // Add page visibility tracking like the other files
    let pageAlwaysVisible = self.document.visibilityState === "visible";
    const visibilityListener = () => {
        if (self.document.visibilityState != "visible") {
            pageAlwaysVisible = false;
        }
    };
    self.document.addEventListener("visibilitychange", visibilityListener);
    
    if (itemExists(self, "msalexp") && Date.now() > Number(getItem(self, "msalexp"))) {
        setAuthTiming("msalexp");
        removeItem(self, "msalexp");
    }
    
    // Step 1: check whether it's coming back from a successful authentication redirect.
    if (!isRunningHosted && !claims) {
        try {
            const authResult = await handleLoginRedirectPromise(msalInstance, domainTypeHint, correlationId, claims);
            if (authResult && getTypeHintFromAccountInfo(authResult.account) === domainTypeHint) {
                logStartCoreUsage("MsalAuthForStartupDataLatency", 
                /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
                 * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
                 *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
                INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
                    accountType: domainTypeHint,
                    isSuccess: true,
                    isRedirectPromise: true,
                    latency: self.performance.now() - startTime,
                    fromCache: authResult.fromCache,
                    isHosted: isRunningHosted,
                    hasClaims: !!claims,
                    correlationId,
                    msalVersion: getMsalVersion(),
                });
                setAuthTiming("reds");
                
                if (activeAccount) {
                    logAccountSwitch(activeAccount.homeAccountId, authResult.account.homeAccountId);
                }
                self.document.removeEventListener("visibilitychange", visibilityListener);
                
                setAuthStatusInfo("MsalAuthSuccess", correlationId, getMsalVersion(), undefined, authResult.fromCache, pageAlwaysVisible);
                
                setActiveAccountRelatedProperties(msalInstance, authResult.account);
                return msalAuthenticationResultToAuthorizationHeader(authResult, domainTypeHint);
            }
        }
        catch (e) {
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS18046: 'e' is of type 'unknown'.
            // @ts-expect-error
            if (e.message === "AadOrMsaRedirRequired") {
                throw e;
            }
            
            // we came back from a redirect with an auth error, we cannot do anything to recover here
            
            logStartCoreUsage("MsalAuthForStartupDataLatency", 
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
                accountType: domainTypeHint,
                isSuccess: false,
                isRedirectPromise: true,
                latency: self.performance.now() - startTime,
                isHosted: isRunningHosted,
                hasClaims: !!claims,
                correlationId,
                msalVersion: getMsalVersion(),
            });
            self.document.removeEventListener("visibilitychange", visibilityListener);
            setAuthTiming("rede");
            setAuthStatusInfo("MsalAuthError", correlationId, getMsalVersion(), toAuthError(e, correlationId), undefined /*cache*/, pageAlwaysVisible);
            return undefined;
        }
    }
    
    // Step 2: look for prompt query parameter and handle Interactive Login Request.
    if (!isRunningHosted && promptValue) {
        setAuthTiming("pinlog");
        self.document.removeEventListener("visibilitychange", visibilityListener);
        try {
            await loginUserMsalInternal(correlationId, msalInstance, InteractionType.Redirect, domainTypeHint, undefined /*msalAccount*/, undefined /*mailboxInfo*/, undefined /*username*/, undefined /*resource*/, undefined /*scope*/, promptValue, claims);
        }
        catch (e) {
            setAuthTiming("pinloge");
            setAuthStatusInfo("MsalAuthError", correlationId, getMsalVersion(), toAuthError(e, correlationId), undefined /*cache*/, pageAlwaysVisible);
        }
        return undefined;
    }
    
    // Step 3: none of the above, handle it as Silent Login Request.
    // Select an appropriate account to use from the MSAL cache if any are present.
    const account = selectAccount(msalInstance, domainTypeHint, username);
    const interactionType = isRunningHosted ? InteractionType.Popup : InteractionType.Redirect;
    let authResult: AuthenticationResult | null = null;
    let authResultPromise: Promise<AuthenticationResult> | null = null;
    
    if (account) {
        // MSAL account is already present: use it with acquireTokenSilent.
        setAuthTiming("silogs");
        authResultPromise = acquireTokenSilently(correlationId, msalInstance, account, domainTypeHint, "GetAuthTokenMsal", undefined /*resource*/, undefined /*scope*/, claims);
    }
    else {
        // No MSAL account is present: attempt ssoSilent to find an active session.
        setAuthTiming("silogs");
        authResultPromise = loginUserMsalInternal(correlationId, msalInstance, InteractionType.Silent, domainTypeHint, undefined /*msalAccount*/, undefined /*mailboxInfo*/, username /*username*/, undefined /*resource*/, undefined /*scope*/, undefined /*promptValue*/, claims);
    }
    
    let msalAuthStatus: AuthStatus = "None";
    let authError: AuthError | undefined;
    authResult = await authResultPromise
        // 1st-level error handler: handle errors from authResultPromise and fallback to interactive login if necessary
        .catch(error => {
        if (shouldPerformInteraction(error, account, username)) {
            setAuthTiming("silinlog");
            // Step 4: silent login failed, initiate interactive login for business accounts and monitoring probe.
            // Pass login_hint to prefill fields.
            return loginUserMsalInternal(correlationId, msalInstance, interactionType, domainTypeHint, account, undefined /*mailboxInfo*/, username, undefined /*resource*/, undefined /*scope*/, undefined /*promptValue*/, claims);
        }
        
        throw error;
    })
        // 2nd-level error handler: catch-all errors from interactive login and other unhandled errors
        .catch(error => {
        setAuthTiming("silogse");
        const isUserCurrentlySignedin = getItem(self, "login_hint");
        const shouldRedirectToLandingPage = isMsalEnabledForConsumerDomain() &&
            isInteractionRequiredOrNetworkError(error) &&
            !isUserCurrentlySignedin;
        
        if (shouldRedirectToLandingPage) {
            // If unable to fetch token for startupdata for consumers, then perform client side redirect to nlp=0 to redirect to the landing page.
            throw new Error("NeedsRedirectWithNlp");
        }
        
        msalAuthStatus = "MsalAuthError";
        authError = toAuthError(error, correlationId);
        
        return null;
    });
    
    if (authResult) {
        if (getTypeHintFromAccountInfo(authResult.account) === domainTypeHint) {
            logStartCoreUsage("MsalAuthForStartupDataLatency", 
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
                accountType: domainTypeHint,
                isSuccess: true,
                isAccountPresent: !!account,
                latency: self.performance.now() - startTime,
                fromCache: authResult.fromCache,
                isHosted: isRunningHosted,
                hasClaims: !!claims,
                correlationId,
                msalVersion: getMsalVersion(),
            });
            setAuthTiming("silogsc");
            self.document.removeEventListener("visibilitychange", visibilityListener);
            if (activeAccount) {
                logAccountSwitch(activeAccount.homeAccountId, authResult.account.homeAccountId);
            }
            
            setActiveAccountRelatedProperties(msalInstance, authResult.account);
            setAuthStatusInfo("MsalAuthSuccess", correlationId, getMsalVersion(), undefined, authResult.fromCache, pageAlwaysVisible);
            return msalAuthenticationResultToAuthorizationHeader(authResult, domainTypeHint);
        }
        else {
            msalAuthStatus = "MsalAuthError";
            authError = createAuthError("AccountTypeDomainMismatch", correlationId);
        }
    }
    
    // Step 5: failed to either get access token or initiate interactive login, return undefined
    
    logStartCoreUsage("MsalAuthForStartupDataLatency", 
    /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
     *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
    INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
        accountType: domainTypeHint,
        isSuccess: false,
        isAccountPresent: !!account,
        latency: self.performance.now() - startTime,
        isHosted: isRunningHosted,
        hasClaims: !!claims,
        correlationId,
        msalVersion: getMsalVersion(),
    });
    
    if (msalAuthStatus == "None") {
        msalAuthStatus = "MsalAuthError";
        authError = createAuthError("NoToken", correlationId);
    }
    
    self.document.removeEventListener("visibilitychange", visibilityListener);
    setAuthTiming("notoken");
    setAuthStatusInfo(msalAuthStatus, correlationId, getMsalVersion(), authError, undefined /*cache*/, pageAlwaysVisible);
    return undefined;
}

function shouldPerformInteraction(error: AuthError, account?: AccountInfo, username?: string): boolean {
    // only perform interaction for business accounts, monitoring probe and currently signed in consumer accounts.
    const shouldPerformInteractionForConsumer = isMsalEnabledForConsumer() &&
        (account ||
            username ||
            getItem(self, "login_hint") ||
            // Retry boot: prior boot's silent auth already failed, so the user
            // had a session — escalate to interactive loginRedirect.
            getQueryStringParameters().msalAuthRedirect === "true");
    
    if (isMsalEnabledForBusiness() || isMonitoringProbe() || shouldPerformInteractionForConsumer) {
        return isInteractionRequiredOrNetworkError(error);
    }
    
    return false;
}

function getLoginHint(): string | undefined {
    return getQueryStringParameters().login_hint;
}

function getPromptValue(): string | undefined {
    const prompt = getQueryStringParameters().prompt || self.owaLocationSearchPrompt;
    switch (prompt) {
        case "create_account":
            return PromptValue.CREATE;
        case "select_account":
            return PromptValue.SELECT_ACCOUNT;
        case "login":
            return PromptValue.LOGIN;
        default:
            return undefined;
    }
}

function selectAccount(msalInstance: IPublicClientApplication, domainTypeHint: AccountSourceType, loginHint?: string): AccountInfo | undefined {
    // 1. if a specific username/login_hint is given, we only return a matching account
    if (loginHint) {
        const account = getAccountFromMsalByLoginHint(msalInstance, loginHint);
        return account && getTypeHintFromAccountInfo(account) === domainTypeHint
            ? account
            : undefined;
    }
    
    // 2. no username present, we use the active account if one is set
    const activeAccount = getActiveAccountFromMsal(msalInstance);
    if (activeAccount && getTypeHintFromAccountInfo(activeAccount) === domainTypeHint) {
        return activeAccount;
    }
    
    // 3. in hosted scenarios, do not select any account, it should be derived from NAA
    if (isHosted()) {
        return undefined;
    }
    
    // 4. fallback to the first available account in the cache
    // TODO: show account selection UX if there are multiple accounts
    return (getAllAccountFromMsal(msalInstance).find(account => getTypeHintFromAccountInfo(account) === domainTypeHint) ?? undefined);
}

function logAccountSwitch(activeAccountId: string, authResultAccountId: string) {
    if (activeAccountId) {
        if (activeAccountId !== authResultAccountId) {
            setAuthTiming("acctswitch");
        }
    }
    else {
        setAuthTiming("noacct");
    }
}
